/*
 * Wishlist → Cart drawer bridge.
 *
 * The iWish (third-party) wishlist app renders each item with a plain
 *   <form action="/cart/add" method="post" class="iwish-product-form">
 *     <input type="submit" class="iwishBuyBtn" ...>
 *   </form>
 * which, on submit, makes the browser POST and redirect to /cart.
 *
 * The theme already supports an AJAX add-to-cart flow that opens the
 * <cart-drawer> on success (see assets/product-form.js, mirrored here).
 * This script intercepts wishlist-form submits, sends the same
 * XHR-style add, and pipes the response into the cart drawer's
 * renderContents() — which both refreshes the drawer contents and
 * opens it.
 *
 * Event delegation on document.body lets this run unconditionally on
 * every page; it is a no-op until the user actually submits a
 * .iwish-product-form.
 */
(function () {
  if (window.__wishlistCartBridgeInstalled) return;
  window.__wishlistCartBridgeInstalled = true;

  // === Wishlist gated behind membership ============================
  // Customer state is exposed via body[data-customer-logged-in] and
  // body.customer-guest (see layout/theme.liquid). When the visitor
  // is a guest:
  //   1. The wishlist page (/apps/wishlist) redirects to the Rewards
  //      page.
  //   2. Any "Add to wishlist" button click on PDP / PLP routes them
  //      to the Rewards page instead of attempting to add.
  // Guests are sent to /pages/rewards (the membership pitch + sign-up
  // / login CTAs) rather than straight to the login form. The header
  // Wishlist link is gated the same way server-side in
  // sections/header.liquid (href flips to /pages/rewards for guests).
  function isGuest() {
    var b = document.body;
    if (!b) return false;
    return b.getAttribute('data-customer-logged-in') === 'false'
      || b.classList.contains('customer-guest');
  }
  function wishlistGateUrl() {
    // Send guests to the Rewards page first, carrying the page they were
    // on as `return_to` so the rewards CTAs can return them there after
    // login. Honour a locale root (e.g. /en) so the link stays in-locale.
    var root = (window.routes && window.routes.root_url) || '/';
    var base = root === '/' ? '/pages/rewards' : root.replace(/\/$/, '') + '/pages/rewards';
    var dest = window.location.pathname + window.location.search;
    return base + '?return_to=' + encodeURIComponent(dest);
  }

  // On the Rewards page, propagate the `return_to` we arrived with onto the
  // login / register CTAs, so after authenticating the customer lands back
  // on the product they tried to wishlist (not just home). The CTAs point at
  // /customer_authentication/login?return_to=… (set in the rewards content);
  // here we just swap in the real destination.
  (function rewriteRewardsLoginLinks() {
    if (!/\/pages\/rewards(\/|$)/.test(window.location.pathname)) return;
    var returnTo = new URLSearchParams(window.location.search).get('return_to');
    if (!returnTo) return;
    var links = document.querySelectorAll('a[href*="/customer_authentication/login"]');
    Array.prototype.forEach.call(links, function (a) {
      try {
        var u = new URL(a.getAttribute('href'), window.location.origin);
        u.searchParams.set('return_to', returnTo);
        a.setAttribute('href', u.pathname + u.search);
      } catch (e) {}
    });
  })();

  // Guests are routed to the Rewards page (not the login form), where
  // they can read the membership pitch and sign up / log in from the
  // page's own CTAs — so no return-to bridge is needed here.
  function isWishlistPage() {
    var p = window.location.pathname || '';
    // Covers /apps/wishlist and /apps/iwish (the iWish proxy uses
    // either depending on installation).
    return /^\/apps\/(wishlist|iwish)(\/|$)/i.test(p);
  }
  // Redirect guests away from the wishlist page itself.
  if (isWishlistPage() && isGuest()) {
    window.location.replace(wishlistGateUrl());
    return;
  }
  // Intercept Add-to-wishlist clicks ONLY for guests. The iWish app
  // uses .product-wishlist-btn (on PDP) and .iWishAdd (on PLP cards
  // / quick-add). We early-return for logged-in customers so their
  // clicks fall through to the app's own handler. Bubble-phase
  // (capture: false) so we don't pre-empt other handlers.
  document.addEventListener('click', function (evt) {
    // Bail immediately for logged-in customers — they should reach
    // the app's add-to-wishlist handler unimpeded.
    if (!isGuest()) return;
    var t = evt.target;
    if (!t || t.nodeType !== 1 || typeof t.closest !== 'function') return;
    var trigger = t.closest('.product-wishlist-btn, .iWishAdd, .iWishAddColl, [data-wishlist-add]');
    if (!trigger) return;
    evt.preventDefault();
    window.location.href = wishlistGateUrl();
  }, false);

  // Tag the wishlist container at runtime so CSS can target it
  // reliably (the iWish app's wrapper class varies by version).
  // Only runs on /apps/wishlist or /apps/iwish — everywhere else
  // there's nothing to tag, so we skip the work entirely.
  function tagWishlistContainer() {
    var items = document.querySelectorAll('.iwishItem');
    if (!items.length) return;
    var parents = new Set();
    items.forEach(function (item) {
      if (item.parentElement) parents.add(item.parentElement);
    });
    parents.forEach(function (el) { el.classList.add('rebrush-wishlist-container'); });
  }
  // Upgrade the small thumbnails the iWish app loads (_x480 ≈ 480px)
  // to a 1500-wide variant so the PLP-sized portrait tiles render
  // sharp on retina displays. The app uses Shopify CDN URLs with
  // either `_x480.jpg` or `_480x480.jpg` suffixes; we rewrite both
  // to `_1500x.jpg`.
  function upgradeWishlistImageSrcs() {
    var imgs = document.querySelectorAll('.iwishItem .iwishImgWrap img');
    if (!imgs.length) return;
    imgs.forEach(function (img) {
      if (img.dataset.resUpgraded === '1') return;
      var src = img.getAttribute('src') || '';
      if (!src || !/cdn\.shopify\.com/.test(src)) return;
      // Rewrite the Shopify size suffix:
      //   _x480.jpg            → _1500x.jpg
      //   _480x480.jpg         → _1500x.jpg
      //   _480x.jpg            → _1500x.jpg
      var upgraded = src.replace(/_(?:\d*x\d+|\d+x\d*|x\d+)(\.(?:jpe?g|png|webp|avif))/i, '_1500x$1');
      if (upgraded !== src) {
        img.setAttribute('src', upgraded);
        img.dataset.resUpgraded = '1';
      }
    });
  }

  if (isWishlistPage()) {
    function runWishlistInit() {
      tagWishlistContainer();
      upgradeWishlistImageSrcs();
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', runWishlistInit);
    } else {
      runWishlistInit();
    }
    // Re-run only if the app injects items asynchronously into the
    // wishlist page itself. Scoped to a known wrapper (or body if
    // not found) and limited to childList so we don't fire on every
    // attribute change site-wide.
    if ('MutationObserver' in window) {
      var observeTarget = document.querySelector('.iwishWrapper') || document.body;
      var mo = new MutationObserver(function () { runWishlistInit(); });
      mo.observe(observeTarget, { childList: true, subtree: true });
    }
  }

  document.addEventListener(
    'submit',
    function (evt) {
      var form = evt.target;
      if (!form || !form.classList || !form.classList.contains('iwish-product-form')) return;

      var cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      // No drawer available on this page → fall back to the default
      // form POST (which redirects to /cart).
      if (!cart) return;

      evt.preventDefault();

      var submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn && submitBtn.getAttribute('aria-disabled') === 'true') return;
      if (submitBtn) {
        submitBtn.setAttribute('aria-disabled', 'true');
        submitBtn.classList.add('loading');
      }

      var sectionIds = cart.getSectionsToRender().map(function (section) { return section.id; });

      var formData = new FormData(form);
      // FormData.append with an Array stringifies inconsistently across
      // browsers (some join with ",", some serialize as "value[]");
      // join explicitly so Shopify reliably treats it as a CSV list.
      formData.append('sections', sectionIds.join(','));
      formData.append('sections_url', window.location.pathname);
      cart.setActiveElement(document.activeElement);

      fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      })
        .then(function (r) { return r.json(); })
        .then(function (response) {
          if (response.status) {
            // Surface error inline next to the submit button.
            var existing = form.querySelector('.iwish-error');
            var message = response.description || response.message || 'Unable to add to cart.';
            if (existing) {
              existing.textContent = message;
            } else {
              var p = document.createElement('p');
              p.className = 'iwish-error';
              p.textContent = message;
              form.appendChild(p);
            }
            return;
          }
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');

          // If the cart-add response didn't include section HTML
          // (e.g. CDN-cached, route mismatch), fall back to fetching
          // the sections directly via the Section Rendering API and
          // open the drawer manually.
          if (response.sections && response.sections[sectionIds[0]]) {
            cart.renderContents(response);
            return;
          }

          fetch('/?sections=' + encodeURIComponent(sectionIds.join(',')), {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
          })
            .then(function (r) { return r.json(); })
            .then(function (sections) {
              cart.renderContents({ id: response.id, sections: sections });
            })
            .catch(function () {
              if (typeof cart.open === 'function') cart.open();
            });
        })
        .catch(function (err) { console.error('[wishlist-cart]', err); })
        .finally(function () {
          if (submitBtn) {
            submitBtn.classList.remove('loading');
            submitBtn.removeAttribute('aria-disabled');
          }
        });
    },
    false
  );
})();
